/**
 * Application Routes Configuration
 *
 * Provides a 3-section sidebar structure:
 * - General: Shared routes available to all venues
 * - Golf Course: Outdoor golf course management (visibility controlled by venue settings)
 * - Bays: Indoor bay/simulator management (visibility controlled by venue settings)
 *
 * Each section can have a settings submenu for section-specific configuration.
 */
import DashboardIcon from "@assets/tsxIcons/DashboardIcon";
import CustomerIcon from "@assets/tsxIcons/CustomerIcon";
import BookingIcon from "@assets/tsxIcons/BookingIcon";
import LessonIcon from "@assets/tsxIcons/LessonIcon";
import EventIcon from "@assets/tsxIcons/EventIcon";
import MembershipIcon from "@assets/tsxIcons/MembershipIcon";
import TransactionIcon from "@assets/tsxIcons/TransactionIcon";
import InboxIcon from "@assets/tsxIcons/InboxIcon";
import AnalyticsIcon from "@assets/tsxIcons/AnalyticsIcon";
import WebsiteCMSIcon from "@assets/tsxIcons/WebsiteCMSIcon";
import PosIcon from "@assets/tsxIcons/PosIcon";
import LeagueManagerIcon from "@assets/tsxIcons/LeagueManagerIcon";
import SocialMiner from "@assets/tsxIcons/SocialMiner";
import MarketingEngine from "@assets/tsxIcons/MarketingEngine";
import ReportingIcon from "@assets/tsxIcons/ReportingIcon";
import TeeTimeIcon from "@assets/tsxIcons/TeeTimeIcon";
import GolfCourseIcon from "@assets/tsxIcons/GolfCourseIcon";
import GolfCartIcon from "@assets/tsxIcons/GolfCartIcon";
import WeatherIcon from "@assets/tsxIcons/WeatherIcon";
import CaddieIcon from "@assets/tsxIcons/CaddieIcon";
import StaffIcon from "@assets/tsxIcons/StaffIcon";

// Types
export type FacilityType = "indoor" | "outdoor" | "hybrid";
export type ContextType = "indoor" | "outdoor";
export type SectionType = "general" | "golf" | "bays";

export interface AppRoute {
  image: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  path: string;
  isComing?: boolean;
  resource?: string;
  action?: string;
  section?: SectionType;
  group?: string;
}

export interface SettingsSubTab {
  title: string;
  key: string;
  path?: string;
}

export interface SettingsRoute {
  title: string;
  path: string;
  hasSubmenu?: boolean;
  subTabs?: SettingsSubTab[];
}

export interface SidebarSection {
  id: SectionType;
  title: string;
  routes: AppRoute[];
  settingsSubmenu?: {
    title: string;
    routes: SettingsRoute[];
  };
}

// =============================================================================
// GENERAL SECTION - Always visible
// =============================================================================
export const generalRoutes: AppRoute[] = [
  {
    image: CustomerIcon,
    title: "Customers",
    path: "/customers",
    isComing: false,
    section: "general",
  },
  {
    image: LessonIcon,
    title: "Lessons",
    path: "/lessons/list",
    isComing: false,
    section: "general",
  },
  {
    image: EventIcon,
    title: "Events Calendar",
    path: "/events/calendar",
    isComing: false,
    section: "general",
  },
  {
    image: MembershipIcon,
    title: "Memberships",
    path: "/membership/plans",
    isComing: false,
    section: "general",
  },
  {
    image: StaffIcon,
    title: "Staff Scheduler",
    path: "/staff-scheduling",
    isComing: false,
    resource: "Staff",
    action: "view",
    section: "general",
  },
];

// Conditional routes for General section based on facility type
export const bookingsRoute: AppRoute = {
  image: BookingIcon,
  title: "Bookings",
  path: "/booking/calendar",
  isComing: false,
  section: "general",
};

export const teeTimesGeneralRoute: AppRoute = {
  image: TeeTimeIcon,
  title: "Tee Times",
  path: "/golf/tee-times",
  isComing: false,
  section: "general",
};

// General settings link (Users & Permissions) - legacy, now in generalRoutes
export const generalSettingsRoute: SettingsRoute = {
  title: "Users & Permissions",
  path: "/settings",
};

// =============================================================================
// GOLF COURSE SECTION - Visible when venue has golf course
// =============================================================================
export const golfCourseRoutes: AppRoute[] = [
  {
    image: DashboardIcon,
    title: "Dashboard",
    path: "/golf/dashboard",
    isComing: false,
    section: "golf",
  },
  {
    image: TeeTimeIcon,
    title: "Tee Times",
    path: "/golf/tee-times",
    isComing: false,
    resource: "Tee Time",
    action: "view",
    section: "golf",
  },
  {
    image: GolfCourseIcon,
    title: "Course Management",
    path: "/golf/courses",
    isComing: false,
    resource: "Golf Course",
    action: "view",
    section: "golf",
  },
  {
    image: GolfCartIcon,
    title: "Cart Fleet",
    path: "/golf/carts",
    isComing: false,
    resource: "Golf Cart",
    action: "view",
    section: "golf",
  },
  {
    image: CaddieIcon,
    title: "Caddies",
    path: "/caddies",
    isComing: false,
    resource: "Caddy",
    action: "view",
    section: "golf",
  },
  {
    image: StaffIcon,
    title: "Staff Scheduler",
    path: "/staff-scheduling",
    isComing: false,
    resource: "Staff",
    action: "view",
    section: "golf",
  },
  {
    image: WeatherIcon,
    title: "Weather",
    path: "/golf/weather",
    isComing: false,
    resource: "Weather",
    action: "view",
    section: "golf",
  },
  {
    image: AnalyticsIcon,
    title: "Analytics",
    path: "/golf/analytics",
    isComing: false,
    section: "golf",
  },
  {
    image: WebsiteCMSIcon,
    title: "Website CMS",
    path: "/golf/website-cms",
    isComing: false,
    section: "golf",
  },
];

// Golf League Manager submenu (rendered in the League Manager side panel,
// mirrors the Settings panel pattern in SettingsSidebarPanel.tsx)
export const golfLeagueRoutes: SettingsRoute[] = [
  { title: "Overview", path: "/golf/leagues/overview" },
  { title: "Live League Night", path: "/golf/leagues/live" },
  { title: "Setup Wizard", path: "/golf/leagues/setup" },
  { title: "Seasons", path: "/golf/leagues/seasons" },
  { title: "Events", path: "/golf/leagues/events" },
  { title: "Member-Guest", path: "/golf/leagues/member-guest" },
  { title: "Schedule Builder", path: "/golf/leagues/schedule" },
  { title: "Handicaps", path: "/golf/leagues/handicaps" },
  { title: "Simulator Bays", path: "/golf/leagues/bays" },
  { title: "Standings", path: "/golf/leagues/standings" },
  { title: "Comms Center", path: "/golf/leagues/comms" },
  { title: "Side Games", path: "/golf/leagues/side-games" },
  { title: "Sponsorships", path: "/golf/leagues/sponsorships" },
  { title: "Pro Shop", path: "/golf/leagues/pro-shop" },
  { title: "Pace of Play", path: "/golf/leagues/pace" },
  { title: "Multi-Club HQ", path: "/golf/leagues/network" },
];

// Golf Settings submenu
export const golfSettingsRoutes: SettingsRoute[] = [
  { title: "Booking Settings", path: "/golf/settings/bookings", hasSubmenu: true, subTabs: [
    { title: "Owner/Operator", key: "admin" },
    { title: "Team Members", key: "employee" },
    { title: "Customers (Website)", key: "customer" },
  ] },
  { title: "Venue", path: "/golf/settings/venue", hasSubmenu: true, subTabs: [
    { title: "General", key: "general" },
    { title: "Bay Management", key: "bay_management" },
    { title: "Payment System", key: "payment_system" },
  ] },
  { title: "Business Hours & Pricing", path: "/golf/settings/pricing", hasSubmenu: true, subTabs: [
    { title: "Daily", key: "daily" },
    { title: "Holidays & Exceptions", key: "holidays" },
    { title: "Price Configuration", key: "price_configuration", path: "/golf/settings/price-configuration" },
  ] },
  { title: "Users & Permissions", path: "/settings", hasSubmenu: true, subTabs: [
    { title: "Current User", key: "current_user" },
    { title: "Team Members", key: "team_members" },
    { title: "Role Permissions Templates", key: "role_templates" },
    { title: "Staff PINs", key: "staff_pins" },
  ] },
  { title: "Language & Regional", path: "/golf/settings/language" },
  { title: "Notifications", path: "/golf/settings/notifications" },
];

// =============================================================================
// BAYS SECTION - Visible when venue has bays/simulators
// =============================================================================
export const bayRoutes: AppRoute[] = [
  {
    image: DashboardIcon,
    title: "Dashboard",
    path: "/bays/dashboard",
    isComing: false,
    section: "bays",
  },
  {
    image: BookingIcon,
    title: "Bookings",
    path: "/booking/calendar",
    isComing: false,
    section: "bays",
  },
  {
    image: AnalyticsIcon,
    title: "Analytics",
    path: "/analytics",
    isComing: false,
    section: "bays",
  },
  {
    image: WebsiteCMSIcon,
    title: "Website CMS",
    path: "/website-cms/branding",
    isComing: false,
    section: "bays",
  },
];

// Bay Settings submenu
export const baySettingsRoutes: SettingsRoute[] = [
  { title: "Booking Settings", path: "/settings/bookings", hasSubmenu: true, subTabs: [
    { title: "Owner/Operator", key: "admin" },
    { title: "Team Members", key: "employee" },
    { title: "Customers (Website)", key: "customer" },
  ] },
  { title: "Venue", path: "/settings/venue", hasSubmenu: true, subTabs: [
    { title: "General", key: "general" },
    { title: "Bay Management", key: "bay_management" },
    { title: "Payment System", key: "payment_system" },
  ] },
  { title: "Business Hours & Pricing", path: "/bookings/operation-hours", hasSubmenu: true, subTabs: [
    { title: "Daily", key: "daily" },
    { title: "Holidays & Exceptions", key: "holidays" },
    { title: "Price Configuration", key: "price_configuration", path: "/settings/pricing" },
  ] },
  { title: "Users & Permissions", path: "/settings", hasSubmenu: true, subTabs: [
    { title: "Current User", key: "current_user" },
    { title: "Team Members", key: "team_members" },
    { title: "Role Permissions Templates", key: "role_templates" },
    { title: "Staff PINs", key: "staff_pins" },
  ] },
  { title: "Language & Regional", path: "/settings/language" },
  { title: "Notifications", path: "/settings/notifications" },
];

// =============================================================================
// COMING SOON ROUTES
// =============================================================================
export const comingSoonRoutes: AppRoute[] = [
  {
    image: SocialMiner,
    title: "Social Miner",
    path: "/social-miner",
    isComing: true,
  },
  {
    image: ReportingIcon,
    title: "Reporting & Analytics",
    path: "/reporting",
    isComing: true,
  },
];

// =============================================================================
// STRUCTURED ROUTES - Returns sections based on venue configuration
// =============================================================================

export interface VenueConfig {
  hasGolfCourse: boolean;
  hasBays: boolean;
  hasEvents?: boolean;
  hasLessons?: boolean;
  hasMemberships?: boolean;
  hasWebsiteBuilder?: boolean;
}

/**
 * Get structured sidebar sections based on venue configuration
 * 
 * Figma Design Layout:
 * 1. Dashboard (top level, no section header)
 * 2. Bays Section: Simulator Bookings, Lessons
 * 3. Golf Course Section: Tee Times, Course Management, Cart Fleet, Caddies, Staff Scheduler
 * 4. Management Section: Inbox, Events, Customers, Memberships, Transactions, Analytics, Website CMS, Users & Permissions
 */
export const getStructuredSections = (config: VenueConfig): SidebarSection[] => {
  const sections: SidebarSection[] = [];

  // ==== TOP LEVEL (Dashboard - no section header) ====
  const dashboardRoute: AppRoute = {
    image: DashboardIcon,
    title: "Dashboard",
    path: "/dashboard",
    isComing: false,
    section: "general",
  };

  // Top level section with Dashboard only (no section title shown)
  sections.push({
    id: "general",
    title: "", // Empty title means no header shown
    routes: [dashboardRoute],
  });

  // ==== BAYS SECTION ====
  if (config.hasBays) {
    const baysRoutes: AppRoute[] = [
      {
        image: BookingIcon,
        title: "Simulator Bookings",
        path: "/booking/calendar",
        isComing: false,
        section: "bays",
      },
    ];

    // League Manager lives under Golf Course when golf is enabled; otherwise surface it under Bays.
    if (!config.hasGolfCourse) {
      baysRoutes.push({
        image: LeagueManagerIcon,
        title: "League Manager",
        path: "/golf/leagues/overview",
        isComing: false,
        section: "bays",
      });
    }

    sections.push({
      id: "bays",
      title: "Bays",
      routes: baysRoutes,
    });
  }

  // ==== GOLF COURSE SECTION ====
  if (config.hasGolfCourse) {
    const golfRoutes: AppRoute[] = [
      {
        image: TeeTimeIcon,
        title: "Tee Times",
        path: "/golf/tee-times",
        isComing: false,
        resource: "Tee Time",
        action: "view",
        section: "golf",
      },
      {
        image: GolfCourseIcon,
        title: "Course Management",
        path: "/golf/courses",
        isComing: false,
        resource: "Golf Course",
        action: "view",
        section: "golf",
      },
      {
        image: GolfCartIcon,
        title: "Cart Fleet",
        path: "/golf/carts",
        isComing: false,
        resource: "Golf Cart",
        action: "view",
        section: "golf",
      },
      {
        image: CaddieIcon,
        title: "Caddies",
        path: "/golf/caddies",
        isComing: false,
        resource: "Caddy",
        action: "view",
        section: "golf",
      },
      {
        image: LeagueManagerIcon,
        title: "League Manager",
        path: "/golf/leagues/overview",
        isComing: false,
        section: "golf",
      },
    ];

    sections.push({
      id: "golf",
      title: "Golf Course",
      routes: golfRoutes,
    });
  }

  // ==== MANAGEMENT SECTION ====
  // Staff Scheduler, Inbox, Customers, Transactions and Analytics are universal
  // items. Service-specific items (Lessons, Events, Memberships, Website CMS)
  // are gated on the tenant's enabled services.
  const managementRoutes: AppRoute[] = [
    {
      image: InboxIcon,
      title: "Inbox",
      path: "/inbox/contact-form",
      isComing: false,
      section: "general",
      group: "Operations",
    },
  ];

  if (config.hasLessons) {
    managementRoutes.push({
      image: LessonIcon,
      title: "Lessons",
      path: "/lessons/list",
      isComing: false,
      section: "general",
      group: "Operations",
    });
  }

  managementRoutes.push({
    image: StaffIcon,
    title: "Staff Scheduler",
    path: "/golf/staff",
    isComing: false,
    resource: "Staff",
    action: "view",
    section: "general",
    group: "Operations",
  });

  managementRoutes.push({
    image: PosIcon,
    title: "POS",
    path: "/pos",
    isComing: false,
    resource: "POS",
    action: "view",
    section: "general",
    group: "Operations",
  });

  if (config.hasEvents) {
    managementRoutes.push({
      image: EventIcon,
      title: "Events",
      path: "/events/calendar",
      isComing: false,
      section: "general",
      group: "Operations",
    });
  }

  managementRoutes.push({
    image: CustomerIcon,
    title: "Customers",
    path: "/customers",
    isComing: false,
    section: "general",
    group: "Business",
  });

  if (config.hasMemberships) {
    managementRoutes.push({
      image: MembershipIcon,
      title: "Memberships",
      path: "/membership/plans",
      isComing: false,
      section: "general",
      group: "Business",
    });
  }

  managementRoutes.push(
    {
      image: TransactionIcon,
      title: "Transactions",
      path: "/transaction",
      isComing: false,
      section: "general",
      group: "Business",
    },
    {
      image: AnalyticsIcon,
      title: "Analytics",
      path: "/analytics",
      isComing: false,
      section: "general",
      group: "Business",
    },
  );

  if (config.hasWebsiteBuilder) {
    managementRoutes.push({
      image: WebsiteCMSIcon,
      title: "Website CMS",
      path: "/website-cms/branding",
      isComing: false,
      section: "general",
      group: "Admin",
    });
  }

  sections.push({
    id: "general" as SectionType, // Re-using general ID for management
    title: "",
    routes: managementRoutes,
  });

  return sections;
};

// =============================================================================
// LEGACY EXPORTS - For backward compatibility
// =============================================================================

// Shared routes - available in all contexts (legacy)
export const sharedRoutes: AppRoute[] = [
  {
    image: DashboardIcon,
    title: "Dashboard",
    path: "/dashboard",
    isComing: false,
  },
  {
    image: CustomerIcon,
    title: "Customers",
    path: "/customers",
    isComing: false,
  },
  {
    image: TransactionIcon,
    title: "Transactions",
    path: "/transaction",
    isComing: false,
  },
  {
    image: InboxIcon,
    title: "Inbox",
    path: "/inbox/contact-form",
    isComing: false,
  },
  {
    image: AnalyticsIcon,
    title: "Analytics",
    path: "/analytics",
    isComing: false,
  },
];

// Indoor-specific routes (legacy)
export const indoorRoutes: AppRoute[] = [
  {
    image: BookingIcon,
    title: "Bookings",
    path: "/booking/calendar",
    isComing: false,
  },
  {
    image: LessonIcon,
    title: "Lessons",
    path: "/lessons/list",
    isComing: false,
  },
  {
    image: EventIcon,
    title: "Events Calendar",
    path: "/events/calendar",
    isComing: false,
  },
  {
    image: MembershipIcon,
    title: "Memberships",
    path: "/membership/plans",
    isComing: false,
  },
];

// Outdoor-specific routes (legacy)
export const outdoorRoutes: AppRoute[] = [
  {
    image: TeeTimeIcon,
    title: "Tee Times",
    path: "/golf/tee-times",
    isComing: false,
    resource: "Tee Time",
    action: "view",
  },
  {
    image: GolfCourseIcon,
    title: "Course Management",
    path: "/golf/courses",
    isComing: false,
    resource: "Golf Course",
    action: "view",
  },
  {
    image: GolfCartIcon,
    title: "Cart Fleet",
    path: "/golf/carts",
    isComing: false,
    resource: "Golf Cart",
    action: "view",
  },
  {
    image: CaddieIcon,
    title: "Caddies",
    path: "/golf/caddies",
    isComing: false,
    resource: "Caddy",
    action: "view",
  },
  {
    image: WeatherIcon,
    title: "Weather",
    path: "/golf/weather",
    isComing: false,
    resource: "Weather",
    action: "view",
  },
  {
    image: StaffIcon,
    title: "Staff Scheduler",
    path: "/golf/staff",
    isComing: false,
    resource: "Staff",
    action: "view",
  },
];

/**
 * Get application routes based on facility type and current context (legacy)
 */
export const getAppRoutes = (
  facilityType: FacilityType,
  currentContext: ContextType
): AppRoute[] => {
  // For indoor-only facilities
  if (facilityType === "indoor") {
    return [
      ...sharedRoutes,
      ...indoorRoutes,
      ...comingSoonRoutes,
    ];
  }

  // For outdoor-only facilities
  if (facilityType === "outdoor") {
    return [
      ...sharedRoutes,
      ...outdoorRoutes,
      ...comingSoonRoutes,
    ];
  }

  // For hybrid facilities - show routes based on current context
  if (currentContext === "indoor") {
    return [
      ...sharedRoutes,
      ...indoorRoutes,
      ...comingSoonRoutes,
    ];
  }

  // Hybrid with outdoor context
  return [
    ...sharedRoutes,
    ...outdoorRoutes,
    ...comingSoonRoutes,
  ];
};

// Legacy export for backward compatibility
export const appRoutes: AppRoute[] = [
  {
    image: DashboardIcon,
    title: "Dashboard",
    path: "/dashboard",
    isComing: false,
  },
  {
    image: CustomerIcon,
    title: "Customers",
    path: "/customers",
    isComing: false,
  },
  {
    image: BookingIcon,
    title: "Bookings",
    path: "/booking/calendar",
    isComing: false,
  },
  {
    image: LessonIcon,
    title: "Lessons",
    path: "/lessons/list",
    isComing: false,
  },
  {
    image: EventIcon,
    title: "Events Calendar",
    path: "/events/calendar",
    isComing: false,
  },
  {
    image: MembershipIcon,
    title: "Memberships",
    path: "/membership/plans",
    isComing: false,
  },
  {
    image: TransactionIcon,
    title: "Transactions",
    path: "/transaction",
    isComing: false,
  },
  {
    image: InboxIcon,
    title: "Inbox",
    path: "/inbox/contact-form",
    isComing: false,
  },
  {
    image: AnalyticsIcon,
    title: "Analytics",
    path: "/analytics",
    isComing: false,
  },
  {
    image: WebsiteCMSIcon,
    title: "Website CMS",
    path: "/website-cms/branding",
    isComing: false,
  },
  {
    image: PosIcon,
    title: "POS",
    path: "/pos",
    isComing: false,
  },
  {
    image: LeagueManagerIcon,
    title: "League Manager",
    path: "/golf/leagues/overview",
    isComing: false,
  },
  {
    image: SocialMiner,
    title: "Social Miner",
    path: "/social-miner",
    isComing: true,
  },
  {
    image: MarketingEngine,
    title: "Marketing Engine",
    path: "/marketing-engine",
    isComing: true,
  },
  {
    image: ReportingIcon,
    title: "Reporting & Analytics",
    path: "/reporting",
    isComing: true,
  },
];

// Golf-specific tab configurations
export const teeTimesTabs = [
  { title: "Calendar", path: "/golf/tee-times/calendar" },
  { title: "List View", path: "/golf/tee-times/list" },
  { title: "Configuration", path: "/golf/tee-times/config" },
];

export const coursesTabs = [
  { title: "Course Information", path: "/golf/courses/info" },
  { title: "Holes", path: "/golf/courses/holes" },
  { title: "Tee Boxes", path: "/golf/courses/tee-boxes" },
  { title: "Scorecard", path: "/golf/courses/scorecard" },
];

export const cartsTabs = [
  { title: "Fleet Command", path: "/golf/carts" },
  { title: "Live Map", path: "/golf/carts/map" },
  { title: "Assignments", path: "/golf/carts/assignments" },
  { title: "Pace of Play", path: "/golf/carts/pace" },
  { title: "Beverage Ops", path: "/golf/carts/beverage" },
  { title: "Messaging", path: "/golf/carts/messaging" },
  { title: "Maintenance", path: "/golf/carts/maintenance" },
  { title: "Telematics", path: "/golf/carts/telematics" },
  { title: "Wash Bay", path: "/golf/carts/wash" },
  { title: "Incidents", path: "/golf/carts/incidents" },
  { title: "Settings", path: "/golf/carts/settings" },
];

export const caddiesTabs = [
  { title: "Roster", path: "/golf/caddies" },
  { title: "Availability", path: "/golf/caddies/availability" },
  { title: "Assignments", path: "/golf/caddies/assignments" },
  { title: "Schedule", path: "/golf/caddies/schedule" },
  { title: "Payroll", path: "/golf/caddies/payroll" },
  { title: "Certifications", path: "/golf/caddies/certifications" },
  { title: "Settings", path: "/golf/caddies/settings" },
];

export const staffSchedulerTabs = [
  { title: "Schedule", path: "/golf/staff" },
  { title: "Time-off", path: "/golf/staff/timeoff" },
  { title: "Open Shifts", path: "/golf/staff/open-shifts" },
  { title: "Staff", path: "/golf/staff/team" },
  { title: "Availability", path: "/golf/staff/availability" },
  { title: "Payroll", path: "/golf/staff/payroll" },
];
